import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { DataManagerController, DataManagerMenuComponent } from '@shared/infra/data-manager';
import { DashboardPreferenciaCardOrigemModel } from '../../../models/dashboard-config.model';
import { DashboardCardModel, DashboardDadoCardItemModel } from '../../../models/dashboard.model';
import { DashboardService } from '../../../services/dashboard.service';

@Component({
  selector: 'app-dashboard-card-list',
  templateUrl: './dashboard-card-list.component.html',
  styleUrl: './dashboard-card-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDragHandle,
    CdkMenuTrigger,
    DataManagerMenuComponent,
    DecimalPipe,
    MatCell,
    MatCellDef,
    MatCheckbox,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatRow,
    MatRowDef,
    MatTable,
    NgTemplateOutlet,
  ],
})
export class DashboardCardListComponent {
  // Inputs
  card = input<DashboardCardModel>();
  dmController = input.required<DataManagerController<DashboardDadoCardItemModel>>();
  fullScreenHabilitado = input(false);
  alternarFullscreen = output();
  origemDados = model<DashboardPreferenciaCardOrigemModel>();

  // Injectables
  private cdr = inject(ChangeDetectorRef);
  private dashboardSvc = inject(DashboardService);
  private elRef = inject(ElementRef);

  // View Child
  protected tableDadosEl = viewChild('tableDados', { read: ElementRef });
  protected tableHeaderEl = viewChild('tableHeader', { read: ElementRef });

  // Atributos private
  private nodesToObserve!: NodeListOf<Element>;
  private resizeObs = new ResizeObserver(this.resizeCallback.bind(this));

  // Signals private
  private refreshNecessario = computed(
    () => {
      return this.dmController().refreshNecessario();
    },
    { equal: () => false },
  );

  // Signals protected
  protected colunas = computed(() => this.buildColunas());
  protected colunasIds = computed(() => this.colunas().map((col) => col.id));
  protected linhas = computed(() => this.buildLinhas());

  protected onOcultarCard() {
    this.dashboardSvc.ocultarCard(this.card()!.id);
  }

  protected onTableDadosScroll(event: Event) {
    const targetEl = event.target as HTMLElement;

    const tabelaDadosEl = this.tableDadosEl()?.nativeElement as HTMLElement;
    if (tabelaDadosEl && tabelaDadosEl.parentElement) {
      tabelaDadosEl.parentElement.scrollTo({
        top: targetEl.scrollTop,
        left: targetEl.scrollLeft,
      });
    }

    const tabelaHeaderEl = this.tableHeaderEl()?.nativeElement as HTMLElement;
    if (tabelaHeaderEl && tabelaHeaderEl.parentElement) {
      tabelaHeaderEl.parentElement.scrollTo({
        left: targetEl.scrollLeft,
      });
    }
  }

  // Métodos private
  private buildColunas() {
    this.setupResizeObserver();
    return this.dmController()
      .colunas()
      .filter((col) => col.tipoColuna !== 'dadoAdicional' || col.sel);
  }

  private buildLinhas() {
    if (!this.refreshNecessario()) {
      // Enquanto não temos dados preenchemos com linhas vazias para o skeleton
      return Array(20);
    }

    // Preciso fazer o refresh quando o status fullscreen é alterado
    const fullScreenChanged = this.fullScreenHabilitado() ? true : true;

    // Necessário para garantir que o markForChek irá executar após os dados
    //   serem atualizados
    setTimeout(() => this.cdr.markForCheck());

    if (fullScreenChanged) {
      this.setupResizeObserver();
      const dados = this.dmController().conteudoVisivel();
      return dados;
    }
    return [];
  }

  private resizeCallback(entries: ResizeObserverEntry[]) {
    const el = this.elRef.nativeElement as HTMLElement;
    entries.forEach((entry) => {
      const entryEl = entry.target as HTMLElement;
      const key = `--col-size-${entryEl.dataset['colIndex']}`;
      el.style.setProperty(key, `${entry.borderBoxSize[0].inlineSize}px`);
    });
  }

  private setupResizeObserver() {
    setTimeout(() => {
      if (this.nodesToObserve) {
        this.nodesToObserve.forEach((el) => {
          this.resizeObs.unobserve(el);
        });
      }

      // TDs da primeira linha da tabela de dados
      const el = this.elRef.nativeElement as HTMLElement;
      this.nodesToObserve = el.querySelectorAll('#table-dados tr:first-of-type td');
      this.nodesToObserve.forEach((entry) => {
        const entryEl = entry as HTMLElement;
        this.resizeObs.observe(entryEl);

        // Largura mínima com base no texto do cabeçalho
        const colIndex = entryEl.dataset['colIndex'];
        if (colIndex) {
          const coluna = this.colunas()[+colIndex];
          const colMinWidth = coluna.descricao.split(' ').reduce((prev, curr) => {
            return prev < curr.length ? curr.length : prev;
          }, 0);
          if (colMinWidth > 0) {
            entryEl.style.minWidth = `${colMinWidth / 2 + 5}rem`;
          }
        }

        // Largura inicial
        const key = `--col-size-${entryEl.dataset['colIndex']}`;
        el.style.setProperty(key, `${entryEl.clientWidth}px`);
      });
    });
  }
}
